<template>
  <section class="player" :class="{'player--is-player-only': isPlayerOnly}">
    <div class="player__background player__background-hero">
      <span></span> <span></span> <span></span> <span></span> <span></span>
    </div>
    <div class="player__wrapper player__wrapper--video" :class="{'is-sticky': isSticky}" ref="wrapper">
      <div class="player__container" :class="{'pre-roll': this.isPreRoll}">
        <div class="adBanner" ref="adbanner"></div>
        <div v-if="!adBlocked" id="the-player"></div>
        <div v-else class="player__container--ad-blocking-message">
          This video is unavailable because we were unable to load a message from our sponsors. <br><br>If you are using ad-blocking software, please disable it and reload the page.
        </div>
        <div v-if="isSticky" class="player__header">
          <div class="player__header-title">{{itemTitle}}</div>
          <button @click="stickyCloseHandler" class="player__close">
            <SVGLoader :src="'close-x-alt.svg'"></SVGLoader>
          </button>
        </div>
      </div>
    </div>
    <div v-if="!isPlayerOnly" class="player__wrapper player__wrapper--title">
      <h1 v-if="!isMoreInfoAvailable" class="player__title player__title--now" :class="{'is-live': resources.isLive}">{{ itemTitle }}</h1>
      <MoreInfo
        v-else
        v-bind:current="resources.current"/>
      <div class="player__title player__title--next">{{ nextTitle }}</div>
      <SocialShare v-bind:current="resources.current"/>
    </div>
  </section>
</template>

<script>
/* global myVideoPlayer:true, uvpjs, vtg, drupalSettings, cbsoptanon */
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import helpers from '../store/store-helpers'
import utils from '../assets/utils'
import adUtils from '../assets/utils/ads'

export default {
  name: 'Player',
  components: { MoreInfo: () => import(/* webpackChunkName: "moreInfo" */ '../components/MoreInfo'), SocialShare: () => import(/* webpackChunkName: "socialShare", webpackPrefetch: true */ '../components/SocialShare'), SVGLoader: () => import(/* webpackChunkName: "svgLoader", webpackPrefetch: true */ '../components/SVGLoader') },
   mounted () {
    this.checkForRedirects()

    if (this.$route.path === '/embed/live') {
      this.doEmbedThings()
    }

    if (this.drupalSettings.enableFMS) {
      // Waits for window.fmscw, exits after 20 tries (ie after 2 seconds).
      if (!window.fmscw && this.fmsCount < 20) {
        setTimeout(() => {
          this.fmsCount++;
        }, 100, this);
        return false;
      }

      if (window.fmscw) {
        window.fmscw.retrieve({
          app: {
            coppa: 0
          }
        }, {
          errorResponseTTL: 3600,
          timeout: 3000
        })
        .then(fmsResponse => {
          this.fmsResponse = fmsResponse
          this.loadPlayer()
        })
        .catch(error => {
          console.error(error);
          // Generate a random ID for fw_vcid2 parameter in case window.fmscw fails.
          this.fmsResponse = {
            fms_params: {
              imafw__fw_vcid2: Math.floor((Math.random() * 1000000) + 1)
            }
          };
        });
      }
    }
    else {
      this.loadPlayer()
    }

    if (this.$refs.adbanner.clientHeight <= 0) {
      this.adBlocked = true
    }

    // Onetrust gate
    if (typeof cbsoptanon !== 'undefined') {
      cbsoptanon.ot.getState((targeting, performance, functional, social) => {
        this.oneTrust = { targeting, performance, functional, social }
      })
    }
  },
  async beforeDestroy () {
    // Remove the player on unmount
    if(myVideoPlayer) {
      this.unlistenToPlayerEvents();
      await cvp.removeVideoPlayer(myVideoPlayer.id);
    }
  },
  computed: {
    ...mapState([
      'isPlaying',
      'isAdPlaying',
      'isPlayerReady',
      'isPlayerOnly',
      'resources',
      'contentTimeForAds',
      'breakpoint',
      'configSettings',
      'daiParams',
      'mediaAutoplayCustom',
      'isPreRoll',
      'default_preview_image',
      'partner',
      'maxAttempts',
      'recreationAttempts',
      'failureContainerId',
      'continuousPlay'
    ]),
    ...mapGetters([
      'wasVideoOrVODPage',
      'isLivePage',
      'isDVRPage',
      'isVODPage',
      'isVideoPage',
      'isLiveVideo',
      'getRouteNameByPath',
      'playlists'
    ]),
    itemTitle () {
      return this.resources.current.StoryHead
    },
    nextTitle () {
      if (this.resources.current.Category !== 'Latest') {
        return this.resources.next !== null ? this.resources.next.StoryHead : this.resources.live.StoryHead
      } else {
        return this.resources.current.UpNext
      }
    },
    isMoreInfoAvailable () {
      return (this.resources.current.Description !== '' && typeof this.resources.current.Description !== 'undefined')
    },
    unmuteButtonText () {
      let bp = this.breakpoint
      if (bp === 'xs' || bp === 'sm') {
        return 'TAP TO UNMUTE'
      }
      return 'CLICK HERE TO UNMUTE'
    },
    async verifyGlobalVars (limit, duration, globalVars) {
      let attempts = 0

      // eslint-disable-next-line consistent-return
      return new Promise(function check(resolve) {
        attempts += 1

        if (globalVars.every((globalVar) => Object.prototype.hasOwnProperty.call(window, globalVar))) {
          return resolve(true)
        }

        if(attempts === limit) {
          return resolve(false)
        }

        setTimeout(() => check(resolve), duration)
      })
    }
  },
  methods: {
    ...mapMutations([
      'setIsPlaying',
      'setIsAdPlaying',
      'addToEndpoints',
      'setMediaAutoplayCustom',
      'setIsPreRoll',
      'setMaxAttempts',
      'setRecreationAttempts',
      'setFailureContainerId',
      'setContinuousPlay'
    ]),
    ...mapActions([
      'whenPlayerIsReady'
    ]),
    /**
     * Adds some redirect logic
     */
    checkForRedirects () {
      // Set up a redirect if path + slug does not match url or its not the video landing page.
      if (!this.isVideoPage && this.resources.current.StoryPath + this.resources.current.StorySlug !== this.$route.path) {
        this.$router.push({
          name: this.getRouteNameByPath(this.$router.options.routes, this.resources.current.StoryPath),
          params: {
            slug: this.resources.current.StorySlug,
            tray: this.resources.tray
          }
        })
      }
    },
    /**
     * Update player preview image size
     */
    getPlayerPreviewImageSize () {
      let bp = this.breakpoint

      switch (bp) {
        case 'xs':
          return '?height=212&width=375'
        case 'sm':
        case 'md':
          return '?height=434&width=768'
        default:
          return '?height=576&width=1024'
      }
    },

    /**
     * Reinit the player
     */
    async reInitPlayer() {
      const container = '#the-player'

      if(myVideoPlayer) {
        this.unlistenToPlayerEvents();
      }
      await cvp.removeVideoPlayer(container);
      //await this.loadPlayer();
    },

    /**
     * Loads the AVIA player.
     */
    loadPlayer () {
      this.setDisplayConfig(this.resources.current)
      try {
        const isAnalyticsVerified = true

        // Create Video Player
         avia.createVideoPlayer({
          container: `#the-player`,
          id: this.failureContainerId || '#the-player',
          playsInline: true,
          autoplay: avia.Autoplay.ATTEMPT_UNMUTED_THEN_MUTED,
          uiConfig: {
            posterUrl: this.default_preview_image + this.getPlayerPreviewImageSize(),
            displayUnmutePromptAtPlayStart: !!avia.Autoplay.ATTEMPT_UNMUTED_THEN_MUTED,
            unmutePromptStyles: 'top: 15px'
          },
          plugins: [{
            name: 'Tracking',
            classRef: isAnalyticsVerified
              ? vtg.ts.Cvp
              : null,
            options: { config: vtg.config },
            priority: cvp.PluginPriority.HIGH
          }]
        }).then((p) => {
           this.onVideoPlayerReady(p)
        })
      }
      catch (error) {
        // Force player recreation under a new container ID
        if(this.recreationAttempts < this.maxAttempts) {
          try {
            this.setRecreationAttempts(this.recreationAttempts + 1)
            this.setFailureContainerId(`#the-player-reinit-${ this.recreationAttempts }`)
          }
          catch(error) {
            console.log('CVP Container Error, Attempting Recreation')
            this.reInitPlayer()
          }
        }
        console.error(error);
      }
    },

    /**
     * Loads and plays a resource.
     */
    loadVideo (resource, isAnalyticsVerified) {
      this.randomStr = this.randomize(1, 999999999)
      this.removeResourceEndEvent()

      if (!myVideoPlayer || typeof this.playlists === 'undefined') {
        return false;
      }

      // Create Resource
      const newResource = this.prepareResourceObject(resource)

      // combine resource metadata with window utag_data
      const final = {...newResource.metadata, ...window.utag_data}

      newResource.metadata = isAnalyticsVerified
        ? {...final, ...(om.getHbParams && om.getHbParams())}
        : final

      // Assign resource to player
      myVideoPlayer.resource = newResource
      this.addResourceEndEvent()
    },

    setDisplayConfig(resource) {
      let device = 'default'
      if (utils.isMobile()) {
        device = 'mobile'
      }

      this.displayMode.autoplay = false
      this.displayMode.muted = false

      if (resource.hasOwnProperty('PlayerConfig')) {
        this.displayMode = resource.PlayerConfig[device]
      }
    },

    /**
     * Listen to Video Player event
     */
    listenToPlayerEvents() {
      const events = cvp.PlayerEvent;

      Object.keys(events).forEach(k => {
        myVideoPlayer.on(events[k], this.handlePlayerEvent);
      });
    },

    /**
     * Unlisten to Video Player event
     */
    unlistenToPlayerEvents() {
      const events = cvp.PlayerEvent;

      Object.keys(events).forEach(k => {
        myVideoPlayer.off(events[k], this.handlePlayerEvent);
      });
    },

    /**
     * Video Player Ready handler
     */
    onVideoPlayerReady (videoPlayer) {
      myVideoPlayer = videoPlayer

      if(myVideoPlayer) {

        // Allow captions to play correctly on desktop.
        if (!navigator.userAgent.match(/(iPad|iPhone|iPod)/g) || !navigator.vendor.match(/(Apple Computer)/g)) {
          //  let vidContainerId = myVideoPlayer.getVidContId()
          //  let videoEl = document.getElementById(vidContainerId).querySelector('video')
          let videoEl = document.getElementById('the-player').querySelector('video')
          videoEl.setAttribute('crossorigin', 'anonymous')
        }

        // Assign Player Events
        this.listenToPlayerEvents()

        // do these things when player is ready
        this.whenPlayerIsReady()

        this.loadVideo(this.resources.current)

        this.addWindowScrollEvent()
        this.addWindowResizeEvent()
      }
    },

    handlePlayerEvent (e) {
      const types = cvp.PlayerEvent

      switch (e.type) {
        // error handling
        case types.AD_ERROR:
        case types.AD_BLOCKED:
        case types.ERROR:
        case types.RESOURCE_ERROR:
        case types.VIDEO_START_ERROR:
        case types.VIDEO_PLAYBACK_ERROR:
          console.error(`CVP Has experienced an error with code ${e.code} and message "${ e.message }"`);
          break;

        // player
        case types.PLAYER_INIT:
          this.onVideoPlayerInit(e)
          break

        // ads
        case types.AD_START:
          this.onAdPodStart()
          break
        case types.AD_POD_END:
          this.onAdPodEnd(e)
          break

        // resource and content
        case types.RESOURCE_START:
          this.onResourceStart()
          break
        case types.CONTENT_PLAYING:
        case types.CONTENT_START:
          this.onContentStart(e)
          break
        case types.RESOURCE_INTERRUPTED:
          this.handleVideoStateChange(e)
          this.setContinuousPlay(0)
          break
        case types.RESOURCE_END:
          this.onResourceEnd()
          this.setContinuousPlay(1)
          break

        // control
        case types.CONTROL_UNMUTE:
          this.handleControlUnmute()
          break

        default:
          // Fall through
      }

    },

    /**
     * Video Player Init Handler
     */
    onVideoPlayerInit (e) {
      // This is important: Keep this reference to the player so you have it
      // when the user clicks another video (image)
      myVideoPlayer = e.target.player
    },

    /**
     * Control Unmute event handler
     */
    handleControlUnmute () {
      this.isMuted = false
    },

    /**
     * RESOURCE_START event handler
     */
    onResourceStart () {
      window.scrollTo(0, 0)
      this.isSticky = false
    },

    /**
     * RESOURCE_END event handler
     */
    onResourceEnd () {
      if (this.resources.next !== null && !this.resources.next.Hidden) {
        this.$router.push({
          name: this.getRouteNameByPath(this.$router.options.routes, this.resources.next.StoryPath),
          params: {
            slug: this.resources.next.StorySlug,
            tray: this.resources.tray
          }
        })
      } else {
        this.$router.push({
          name: this.getRouteNameByPath(this.$router.options.routes, this.playlists[0].items[0].StoryPath),
          params: {
            slug: this.playlists[0].items[0].StorySlug,
            tray: this.playlists.category
          }
        })
      }
      this.setMediaAutoplayCustom(true)
    },

    /**
     * RESOURCE_INTERRUPTED event handler
     */
    handleVideoStateChange (e) {
      if (e.target.playbackState === avia.PlaybackState.PLAYING && !this.isPlaying) {
        this.setIsPlaying(true)
        this.startAdTimer()
      } else if (this.isPlaying) {
        this.setIsPlaying(false)
        this.stopAdTimer()
      }
    },

    /**
     * Click Handler for sticky player's close button
     */
    stickyCloseHandler () {
      this.disableSticky = true
      this.isSticky = false
    },

    /**
     * Mute Button Handler
     */
    unmuteClickHandler () {
      myVideoPlayer.unMute()
      this.isMuted = false
    },

    /**
     * AD__POD_START event handler
     */
    onAdPodStart () {
      if (!this.resources.isLive && utils.isMobile()) {
        this.setIsPreRoll(true)
      }
      this.setIsAdPlaying(true)
      this.adBlocked = false
    },

    /**
     * AD_END event handler
     */
    onAdPodEnd (e) {
      if (e.target.adPod.hasPlayed) {
        this.playTime = 0
      }
      this.setIsPreRoll(false)
      this.setIsAdPlaying(false)
    },

    /**
     * Playlist Recommendation Handler
     */
    onPlaylistRecommendation (e) {
      // load the recommendation list
      var pl = e.payload.playlist
    },


    /**
     * CONTENT_START event handler
     */
    onContentStart (e) {

    },

    /**
     * Adds the RESOURCE_END event listener to the video player.
     */
    addResourceEndEvent () {
      myVideoPlayer.on(cvp.PlayerEvent.RESOURCE_END, this.handlePlayerEvent)
    },

    /**
     * Removes the RESOURCE_END event listener to the video player.
     */
    removeResourceEndEvent () {
      myVideoPlayer.off(cvp.PlayerEvent.RESOURCE_END, this.handlePlayerEvent)
    },

    /**
     * Adds a scroll event listener to the window object.
     */
    addWindowScrollEvent () {
      window.addEventListener('scroll', this.stickyHandler)
    },

    /**
     * Adds a resize event listener to the window object.
     */
    addWindowResizeEvent () {
      this.stickyThreshold = this.$refs.wrapper.offsetTop + (this.$refs.wrapper.offsetHeight / 2)
      if (utils.isIOS()) {
        window.addEventListener('orientationchange', this.stickyHandler)
        window.addEventListener('orientationchange', this.updateStickyThreshold)
      } else {
        window.addEventListener('resize', this.stickyHandler)
        window.addEventListener('resize', this.updateStickyThreshold)
      }
    },

    /**
     * Scroll and resize handler for sticky player - toggles sticky player on and off.
     */
    stickyHandler (e) {
      let topPos

      if (e.target.scrollingElement) {
        topPos = e.target.scrollingElement.scrollTop
      } else {
        topPos = window.pageYOffset
      }

      if (!this.disableSticky) {
        if ((this.stickyThreshold <= topPos) && !this.isSticky) {
          this.isSticky = true
        } else if ((this.stickyThreshold > topPos) && this.isSticky) {
          this.isSticky = false
        }
      }
      if ((this.stickyThreshold > topPos) && this.disableSticky) {
        this.disableSticky = false
      }
    },

    /**
     * Changes threshold depending on device orientation
     */
    updateStickyThreshold () {
      import('../assets/utils').then(utils => {
        utils.default.debounce(this, () => {
          this.stickyThreshold = this.$refs.wrapper.offsetTop + (this.$refs.wrapper.offsetHeight / 2)
        }, 100)
      })
    },

    /** HELPER FUNCTIONS **/
    /**
     * Click to view live video
     *
     * @returns {string}
     */
    prepareAdCallUrl (resource, network="dfp", id3 = false, payload = null) {
      let npaFlag = 0;
      let customParams = null;
      let resultAdCall = ''

      // Check for Onetrust Acceptance and set NPA flag if enabled.
      if (typeof cbsoptanon !== 'undefined' && cbsoptanon !== null) {
        cbsoptanon.ads.getNpaFlag((flag) => {
        npaFlag = flag !== 0 ? flag : 0
        });
      }

      if(!resource.EnablePreRolls) {
        return null
      }

      switch(network) {
        case 'dfp':
        case 'ima':
            let adCall = ''
            let platformId = 8264
            let departmentNamespace = 'vaw-ctd'
            let deviceType = !utils.isMobile() ? 'desktop' : 'mobile_web'
            let playerBrand = 'insideedition'
            let ppValue = 'vpaid_js'
            let segmentationUpid = '0100'
            if (payload) {
              if (payload.hasOwnProperty('segmentationUpid') && payload.segmentationUpid.length) {
                segmentationUpid = '0' + payload.segmentationUpid[0]
              }
            }

            // Don't strip categories. Return true.
            let tags = resource.AdTags.filter(function (tag) {
              return true
            })

            tags = encodeURIComponent(tags)

            let cmsID = (typeof window.drupalSettings.playerConfig.cmsid === 'undefined') ? '2289' : window.drupalSettings.playerConfig.cmsid

            let vID = resource.StoryId.toString()

            if (this.isLivePage) {
              vID = this.configSettings.mpxDvrID
            }
            if (this.resources.isLive) {
              vID = this.configSettings.mpxLiveID
            }

            let vguidValue = adUtils.videoPlayerGetValueFromPage('vguid_value')
            let ftagValue = adUtils.videoPlayerGetValueFromPage('ftag_value')
            adCall = 'https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/' +
                    platformId + '/' +
                    departmentNamespace + '/' +
                    deviceType + '/' +
                    playerBrand +
                    '&ad_rule=1&impl=s&gdfp_req=1&env=vp&output=xml_vast4&unviewed_position_start=1&url=' +
                    document.referrer +
                    '&video_url_to_fetch=' +
                    window.location.origin + window.location.pathname + encodeURIComponent(window.location.search) +
                    '&vid=' +
                    vID +
                    '&cust_params=site%3Dctd%26show%3D' +
                    playerBrand +
                    '%26keywords%3D' +
                    tags +
                    '%26ptype%3D' +
                    'video' +
                    '%26section%3Dvideo' +
                    '%26vguid%3D' +
                    vguidValue +
                    '%26ftag%3D' +
                    ftagValue +
                    '%26env%3D' +
                    drupalSettings.ctdBidBarrel.page_targeting.env +
                    '%26device%3D' +
                    deviceType +
                    '&cmsid=' +
                    cmsID +
                    '&npa=' +
                    npaFlag +
                    '&pp=' +
                    ppValue +
                    '&description_url=' +
                    window.location +
                    '&url=' +
                    window.location +
                    '&host=' +
                    window.location.origin +
                    '&session=&subses=&bidid=&feat=' + segmentationUpid +
                    '&correlator=' + this.randomStr +
                    '&scor=' + this.randomStr

            if (resource.economics === 'FREE') {
              return null
            }

            if (((this.playTime >= this.contentTimeForAds || this.wasVideoOrVODPage) && !this.resources.isLive) || (id3 && !utils.isMobile())) {
              resultAdCall = adCall
            }
            break;
        case 'freewheel':
            let adurl = new URL(this.drupalSettings.fw_base_url)
            let fwNetworkId = this.drupalSettings.fw_network_id
            let fwBrand = this.drupalSettings.fw_brand
            let caid = resource.StoryId
            // IE Video API doesn't have ReferenceID, it uses StoryId for the MPX reference ID
            let description_url = this.drupalSettings.fw_desc_url
            let keywords = resource.AdTags ? resource.AdTags.join().toLowerCase().replace('&', 'and').replace(/ /g, '_').replace(/,\s*$/, "").split(',') : null

            // Global Params
            adurl.searchParams.append('asnw', fwNetworkId)
            adurl.searchParams.append('caid', caid);
            adurl.searchParams.append('csid', `vcbs_${ fwBrand }_${ utils.isMobile() ? 'mobile' : 'desktop' }_web_${ this.$route.path === '/embed/live' ? 'vod_embed' : 'vod'}`);
            adurl.searchParams.append('flag', '+amcb+emcr+sltp+slcb+qtcb+vicb+fbad+sync+nucr+aeti');
            adurl.searchParams.append('metr', 1023)
            adurl.searchParams.append('nw', fwNetworkId)
            adurl.searchParams.append('prof', `${ fwNetworkId }:GoogleDAICSAI_v01`)
            adurl.searchParams.append('pvrn', `${this.randomize(1000000000000000, 9000000000000000)}`)
            adurl.searchParams.append('resp', 'vmap1+vast4')
            adurl.searchParams.append('ssnw', fwNetworkId)

            // Key Values
            adurl.searchParams.append('vprn', `${this.randomize(1000000000000000, 9000000000000000)};_fw_continuous_play=${ this.continuousPlay || 0 }`)
            adurl.searchParams.append('_fw_h_referer', window.location.href)
            adurl.searchParams.append('_fw_is_lat', npaFlag)
            adurl.searchParams.append('npa', npaFlag)

            // Custom Params
            adurl.searchParams.append('cpPre', 0)
            adurl.searchParams.append('cpSession', this.continuousPlay || 0)
            adurl.searchParams.append('description_url', description_url)
            adurl.searchParams.append('embed', 0)
            adurl.searchParams.append('partner', fwBrand)
            adurl.searchParams.append('section', window.utag_data.siteSection)
            adurl.searchParams.append('show', fwBrand)
            adurl.searchParams.append('site', 'ctd')
            adurl.searchParams.append('sz', '640x480')
            if (keywords) {
              for (const keyword of keywords) {
                if(keyword.length) {
                  adurl.searchParams.append('keywords', keyword);
                }
              }
            }
            adurl.searchParams.append('ptype', window.utag_data.pageType)
            adurl.searchParams.append('vguid', window.utag_data.pageViewGuid)
            adurl.searchParams.append('ftag', window.utag_data.campaign? window.utag_data.campaign : false)
            // FMS Values
            if (this.fmsResponse && this.fmsResponse.fms_params) {
              for (const [k, v] of Object.entries(this.fmsResponse.fms_params)) {
                adurl.searchParams.append(k, v)
              }
            }

            const regex = /imafw_/g
            adurl.href = adurl.href.replace(regex, '')
            resultAdCall = adurl ? adurl.href.replace('%3B', ';').replace('__fw_vcid2', '_fw_vcid2').replace('_fw_continuous_play%3D', '_fw_continuous_play=') : null
            break;
          default:
            console.log(`The selected ad network "${ network }" is not supported.`)
      }

      return resultAdCall

    },

    /**
     * Converts timecode to seconds
     *
     * @returns {int}
     */
    convertToSeconds (hms) {
      let seconds = 0
      if(hms) {
          let t = hms.split(':')
          seconds = (+t[0]) * 60 * 60 + (+t[1]) * 60 + (+t[2])
          return seconds
      }
    },

    /**
     * Starts preroll ad's minimum playtime timer
     */
    startAdTimer () {
      clearInterval(this.timer)
      this.timer = setInterval(() => {
        if (!this.isAdPlaying) {
          this.playTime += 1
        }
      }, 1000)
    },

    /**
     * Stops preroll ad's minimum playtime timer
     */
    stopAdTimer () {
      clearInterval(this.timer)
    },

    /**
     * This is to determine a value for tracking
     *
     * @returns {string}
     */
    getMediaContentType () {
      if (this.isDVRPage) {
        return 'dvr'
      } else {
        return 'vod'
      }
    },

    /**
     * Generates a random string
     *
     * @returns {string}
     */
    randomize (min, max) {
      return Math.floor((Math.random() * (max - min) + min)).toString()
    },

    /**
     * Get video index in its playlist
     *
     * @returns {string}
     */
    getVideoIndex (resource) {
      const currentPlaylist = this.playlists.filter(pl => pl.category === this.resources.tray)
      if (currentPlaylist && currentPlaylist.length > 0 && currentPlaylist[0].items)
        return currentPlaylist[0].items.findIndex(rco => rco.StorySlug === resource.StorySlug)
      else
        return 0
    },

    /**
     * Prepares resource object for player consumption.
     * @param {object} resource
     * @returns {object}
     */
    prepareResourceObject (resource) {
      const mainRCO = avia.createResourceConfig()

      mainRCO.location.mediaUrl = resource.AssetURL
      mainRCO.mediaId = resource.StoryId.toString(),
      mainRCO.metadata = {
        videoTitle: resource.StoryHead,
        videoIdForTracking: resource.StoryId,
        videoId: resource.StoryId,
        duration: this.resources.isLive ? -1 : this.convertToSeconds(resource.Duration),
        vidPlaylistPos: this.getVideoIndex(resource),
        vidContID: 'the-player',
        vidPlayerPos: 'hero',
        category: resource.Category,
        fileType: 'video',
        cmsRefGuid: resource.StoryId,
        // old contextMetadata
        assetId: resource.StoryId.toString(),
        mediaAutoplayCustom: this.mediaAutoplayCustom.toString(),
        sitePrimaryRsid: 'cbsiinsideeditionsite',
        brandPlatformId: window.utag_data.brandPlatformId,
        siteType: window.utag_data.siteType,
        deviceType: window.utag_data.deviceType,
        siteSection: window.utag_data._siteSection,
        mediaRegion: resource.Category.toLowerCase(),
        assetType: 'video',
        mediaContentType: this.getMediaContentType(),
        streamType: 'vod',
        assetTitle: resource.StoryHead,
        siteHier: window.utag_data.siteHier,
        pageType: window.utag_data.pageType,
        pageViewGuid: window.utag_data.pageViewGuid,
        assetPubDate: (new Date(resource.StartTime * 1000)).toISOString(),
        userState: window.utag_data.userState,
        userType: window.utag_data.userType,
        pageName: 'insideedition :' + document.location.pathname,
        pageURL: window.self !== window.top ? document.referrer : document.location.href,
        'cm.ssf' : this.oneTrust.targeting ? '0' : '1'
      }

      const network = this.drupalSettings.adProvider
      if(!this.adBlocked) {
        mainRCO.ad.context = avia.AdContext.IMA

        if(network === 'freewheel') {
          mainRCO.ad.csai.adCallUrl = this.prepareAdCallUrl(resource, network, false, null)
        }
        else {
          mainRCO.ad.ima.adCallUrl = this.prepareAdCallUrl(resource, network, false, null)
        }
      }

      // Captions
      if(resource.CaptionsURL) {
        mainRCO.location.timedTextUrl = resource.CaptionsURL;
      }

      if (resource.CaptionsURL && (utils.isIE() || navigator.userAgent.match(/Android/i))) {
        mainRCO.closedCaptionPath = resource.CaptionsURL
        mainRCO.closedCaptionType = "text/vtt"
      }

      return mainRCO
    }
  },
  watch: {
    resources (val, oldVal) {
      if (typeof myVideoPlayer !== 'undefined') { // fix this
        if ((!oldVal.isLive || !val.isLive) && this.isPlayerReady) {
          this.loadVideo(val.current)
        }
      }
    }
  },
  data () {
    return {
      infoAvailable: false,
      internalNS: '.ctd', // @todo need to change
      playerOptions: {},
      sessionOptions: {
        partner: 'cbs',
        skin: true,
        autoPlay: false,
        previewImage: this.default_preview_image + this.getPlayerPreviewImageSize(),
        playsInline: true
      },
      displayMode: {},
      isMuted: false,
      oneTrust: {},
      isSticky: false,
      stickyThreshold: 0,
      adBlocked: false,
      disableSticky: false,
      playerContainer: 'the-player',
      playTime: this.contentTimeForAds,
      timer: null,
      randomStr: this.randomize(1, 9999999),
      sessionId: utils.randomAlphaNumberic(40),
      drupalSettings: drupalSettings || window.drupalSettings,
      fmsResponse: null,
      fmsCount: 0
    }
  }
}
</script>

<style lang="sass">
  @import '../assets/scss/_uvpjs.scss'
  @import '../assets/scss/components/Player.scss'
</style>
